import { datadogLogs } from '@datadog/browser-logs';
import type { Address, Contact } from '@prisma/client';
import { BillingPeriodStatus, FileType, PaymentMethod, PricingType, ScheduledJobType, TimecardEntryType } from '@prisma/client';
import type { Decimal } from '@prisma/client/runtime/library';
import type { CustomerForQuoting } from '@schema/customer.schema';
import { capitalizeFirstLetter } from '~/components/utils';
import { FieldJobType } from '~/server/schema/field.schema';
import { money } from './math';

// Compares string-typed "number" fields (invoice #, PO #, delivery #, etc.) the way a human expects: digit runs
// compare by magnitude rather than character-by-character, so "D2" sorts before "D10" and "IN-20000-1" sorts
// before "IN-100000-1". Plain `localeCompare`/`<` on these fields sorts lexicographically instead, which looks
// broken once the numeric portion crosses a digit-count boundary (e.g. all "1xxxxx" values group before any
// "2xxxxx"-"9xxxxx" value, regardless of actual size).
export const naturalCompare = (a: string | null | undefined, b: string | null | undefined): number =>
	(a ?? '').localeCompare(b ?? '', undefined, { numeric: true });

export const titlifyEnum = (val: string) => {
	const spacedOut = val.replace(/_/g, ' ').toLowerCase();
	return spacedOut.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()).trim();
};

// CHECK → "Check", CREDIT_CARD → "Credit Card", ACH stays an initialism.
export const paymentMethodLabel = (method: PaymentMethod | string) => (method === PaymentMethod.ACH ? 'ACH' : titlifyEnum(method));

export const allCapsTitlifyEnum = (val: string) => {
	const spacedOut = val.replace(/_/g, ' ').toUpperCase();
	return spacedOut.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()).trim();
};

export const perUnitLabel = (priceType: PricingType) => titlifyEnum(priceType === PricingType.PER_SQUARE ? 'SQUARE' : priceType);

export const titlifyPriceType = (priceType: PricingType) => {
	if (priceType === PricingType.SQUAREFOOT) return 'SQ.FT';
	if (priceType === PricingType.LINEAR_INCH) return 'Lin. Inch';
	if (priceType === PricingType.UNITED_INCH) return 'Utd. Inch';

	return titlifyEnum(priceType);
};

export const billingPeriodStatusLabel = (status: BillingPeriodStatus): string => {
	switch (status) {
		case BillingPeriodStatus.PENDING:
			return 'Draft';
		case BillingPeriodStatus.ONGOING:
			return 'Partially Paid';
		case BillingPeriodStatus.PAID:
			return 'Paid (auto)';
		case BillingPeriodStatus.INVOICED:
			return 'Invoiced';
	}
};

export const titlifyTimecardEntryType = (type: TimecardEntryType) => {
	if (type === TimecardEntryType.REGULAR) return 'Work';
	if (type === TimecardEntryType.FABRICATION) return 'In Office';

	return titlifyEnum(type);
};

// ENUM_VAL to enum-val
export const enum2htmlid = (enumVal: string) => {
	return enumVal.replace(/_/g, '-').toLowerCase();
};

export const camel2Title = (camelCaseVal: string) => {
	return camelCaseVal.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
};

export const installTimeShort = (installMinutesRequired: number) => {
	return `${Math.floor(installMinutesRequired / 60)}h:${installMinutesRequired % 60}m`;
};

// Force free text to dash-separated-format
export const text2id = (text: string) => {
	return text
		.toLowerCase() // lower case
		.replace(/[^a-zA-Z0-9 ]/g, '') // remove special characters
		.replace(/[-_:]/g, ' ') // separators to spaces
		.replace(/\W+/g, '-'); // whitespace to dashes
};

export const formatInches = (value: string) => {
	const trimmedValue = value.trim();
	const lastCharacter = trimmedValue.charAt(trimmedValue.length - 1);

	if (lastCharacter === '"') return value;

	return `${value}"`;
};

/**
 * Round a non-currency value to a set number of digits.
 *
 * @param num Number to round (Eg: 1.2345)
 * @param trailingDigits Number of digits (2)
 * @returns "1.23"
 */
export const roundToDigits = (num: number, trailingDigits: number) => {
	const factor = 10 ** trailingDigits;
	return (Math.round(num * factor) / factor).toString();
};

/** `1` → `"1st"`, `12` → `"12th"`, `22` → `"22nd"`. */
export const ordinal = (day: number): string => {
	if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`;
	switch (day % 10) {
		case 1:
			return `${day}st`;
		case 2:
			return `${day}nd`;
		case 3:
			return `${day}rd`;
		default:
			return `${day}th`;
	}
};

export const truncToDigits = (num: number, trailingDigits: number) => {
	const factor = 10 ** trailingDigits;
	return Math.trunc(num * factor) / factor;
};

export const formatDisplayPhone = (phoneNumber: string | null | undefined, extension?: string | null) => {
	if (!phoneNumber) return '';
	const cleaned = ('' + phoneNumber).replace(/\D/g, '');
	const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
	const formattedExtension = extension ? ` x${extension}` : '';
	if (match) {
		return ['(', match[2], ') ', match[3], '-', match[4], formattedExtension].join('');
	}
	return `${phoneNumber}${formattedExtension}`;
};

export const normalizePhoneNumber = (phoneNumber: string | null | undefined): string => {
	if (!phoneNumber) return '';
	const cleaned = phoneNumber.replace(/\D/g, '');
	return cleaned.startsWith('1') && cleaned.length === 11 ? cleaned.slice(1) : cleaned;
};

/**
 * Parses a phone value from an imported spreadsheet cell that may carry arbitrary formatting and a
 * leading label into a normalized 10-digit `phone`. `valid` is true only for a 10-digit number so
 * callers can flag unusable rows for review instead of storing a broken number. Extensions are
 * deliberately not supported — a value
 * with extra digits (e.g. a trailing extension) simply fails the length check and is flagged
 * invalid. Because normalizePhoneNumber already drops a leading US country-code "1", a value that
 * is still 11 digits is a non-US (or malformed) number and is likewise flagged invalid rather than
 * stored — downstream SMS delivery expects the standard 10-digit format.
 *
 * Handles e.g. "555.123.4567", "(555) 123-4567", "1-555-123-4567", and "Work: 555-123-4567".
 */
export const parseImportedPhoneForSMS = (raw: string | null | undefined): { phone: string | null; valid: boolean } => {
	if (!raw || raw.trim().length === 0) {
		return { phone: null, valid: false };
	}

	// Drop a leading "Label:" prefix (e.g. "Work: 555-1234") so digits in the label don't pollute the number.
	const base = raw.replace(/^[^:]*:/, '');

	// normalizePhoneNumber strips non-digits and drops a leading US country-code "1".
	const digits = normalizePhoneNumber(base);
	const valid = digits.length === 10;

	return { phone: valid ? digits : null, valid };
};

/** "3", "invoice" → "3 invoices"; "1", "invoice" → "1 invoice". Naive English -s pluralization. */
export const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

export const formatUSD = (value?: number | Decimal, removeChange?: boolean) => {
	const formatter = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	});

	const valueWithPrecision = money(Number(value ?? 0));
	const formattedValue = formatter.format(valueWithPrecision);

	return removeChange ? formattedValue.replace(/\.[0-9]{2}$/, '') : formattedValue;
};

export const formatSignedUSD = (value?: number) => {
	const amount = Number(value ?? 0);
	const formatted = formatUSD(Math.abs(amount));

	return amount < 0 ? `–${formatted}` : `+${formatted}`;
};

const denominations: { suffix: string; value: number }[] = [
	{ suffix: 'B', value: 1000000000 },
	{ suffix: 'M', value: 1000000 },
	{ suffix: 'k', value: 1000 },
];
export const formatUSDWithDenomination = (value?: number) => {
	const numericValue = value ?? 0;
	const denomination = denominations.find((denomination) => Math.abs(numericValue) >= denomination.value);

	return denomination
		? `${formatUSD(numericValue / denomination.value).replace(/\.00$/, '')}${denomination.suffix}`
		: formatUSD(numericValue, true);
};

export const formatNumberToPercent = (value: number) => {
	return `${Math.round(value * 100)}%`;
};

export const formatLengthInInches = (value: string) => {
	return value.endsWith('"') ? value : `${value}"`;
};

/*
Turn a search string into a regex with non-alphanumeric characters removed.
*/
export const tokenizedRegex = (searchString: string) => {
	const nontokens = /[^a-z0-9]+/gi;
	const tokens = searchString.split(nontokens);
	return RegExp(tokens.join('.*'), 'i');
};

export function convertToSize(bytes: number | null) {
	if (bytes === 0 || bytes === null) return '0';
	const k = 1024,
		sizes = ['', 'KB', 'MB', 'GB', 'TB'], // Don't think we'll ever get to TB, but just in case
		i = Math.floor(Math.log(bytes) / Math.log(k));
	return (bytes / Math.pow(k, i)).toPrecision(3) + ' ' + sizes[i];
}

interface IAddress {
	street1: string;
	street2?: string | null;
	city: string;
	state: string | null;
	zip: string;
}

export function generateAddressString(address: IAddress, separator = '\n', name?: string): string {
	if (!address) {
		datadogLogs.logger.error('Undefined/null/falsy address being passed to PDF address generator.');
		return '';
	}

	return `${name ? `${name}${separator}` : ''}${address.street1 ? address.street1 : ''}${address.street2 ? `${separator}${address.street2}` : ''}${separator}${
		address.city ? address.city : ''
	}, ${address.state ? address.state : ''} ${address.zip ? address.zip : ''}`;
}

// Necessary because some addresses in db don't have city, state, or zip
export function isDisplayableAddress(address?: Address | null): address is Address {
	if (!address) {
		return false;
	}

	return !!(address.street1 && address.city && address.state && address.zip);
}

export function generateCustomerContactString(customer: Pick<CustomerForQuoting, 'billingAddress' | 'contacts' | 'isCommercial' | 'name'>): string {
	const customerDefaultContact = customer.contacts?.find((c) => c.isDefault);
	return `${customer.isCommercial ? `${customer.name}` : `${customerDefaultContact?.name}`}${
		customerDefaultContact?.phone ? `\n${formatDisplayPhone(customerDefaultContact.phone, customerDefaultContact.phoneExtension)}` : ''
	}${customerDefaultContact?.email ? `\n${customerDefaultContact.email}` : ''}${
		isDisplayableAddress(customer.billingAddress) ? `\n\n${generateAddressString(customer.billingAddress)}` : ''
	}`;
}

export function generateCustomerContactStringForContact(
	customer: Pick<CustomerForQuoting, 'billingAddress' | 'isCommercial' | 'name'>,
	contact: Contact
): string {
	return generateCustomerContactString({ ...customer, contacts: [{ ...contact, isDefault: true }] });
}

export function generateContactString(contacts: Contact | Contact[]): string {
	if (Array.isArray(contacts)) {
		return contacts
			.map(
				(contact) =>
					`${contact?.name ? `${contact.name}` : ''}${contact?.phone ? `\n${formatDisplayPhone(contact.phone)}` : ''}${
						contact?.email ? `\n${contact.email}` : ''
					}`
			)
			.join('\n\n');
	} else {
		const contact = contacts as Contact;
		return `${contact?.name ? `${contact.name}` : ''}${contact?.phone ? `\n${formatDisplayPhone(contact.phone)}` : ''}${
			contact?.email ? `\n${contact.email}` : ''
		}`;
	}
}

// Composes the Bill To string from the customer's primary contact plus any selected
// (non-default) contacts. Shared by the invoice form's BillToInput and EstimateQuoteView's
// initial seeding so selections stay consistent when switching between quote and invoice.
export function generateBillToString(customer: CustomerForQuoting, selectedContactIds: number[]): string {
	const selectedContacts = (customer.contacts ?? []).filter((contact) => selectedContactIds.includes(contact.id) && !contact.isDefault);
	return [generateCustomerContactString(customer), ...selectedContacts.map((contact) => generateContactString(contact))]
		.filter(Boolean)
		.join('\n\n');
}

// NB: Doesn't handle all cases, but if you want to try add more tests
export function toCamelCase(str: string) {
	const [first, ...rest] = str.split(/[^a-zA-Z0-9]/).filter((s) => s.length > 0);

	if (first === undefined) return '';
	return [
		first.replace(/^(\w{1})/g, (letter) => letter.toLowerCase()),
		...rest.map((word) => word.replace(/^\w{1}/g, (letter) => letter.toUpperCase())),
	].join('');
}

export function enumToCamelCase(str: string) {
	const [first, ...rest] = str.toLowerCase().split('_');

	if (first === undefined) return '';
	return [first, ...rest.map((word) => word.replace(/^\w{1}/g, (letter) => letter.toUpperCase()))].join('');
}

export function convertMillisecondsToTime(ms: number) {
	const seconds = Math.floor(ms / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);

	if (hours > 0) {
		return `${hours} hour${hours > 1 ? 's' : ''}`;
	} else if (minutes > 0) {
		return `${minutes} minute${minutes > 1 ? 's' : ''}`;
	} else {
		return 'Less than a minute';
	}
}

export function kebabToTitleCase(kebabStr: string): string {
	return kebabStr
		.split('-') // split the string into words
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize the first character of each word
		.join(' '); // join the words back into a string, separated by spaces
}

export function toTitleCase(string: string) {
	return string.replace(/\w\S*/g, function (text) {
		return text.charAt(0).toUpperCase() + text.substring(1).toLowerCase();
	});
}

export function readFileExtension(fileName: string) {
	if (!fileName.includes('.') || fileName === '.') {
		return '';
	}

	// remove any and all trailing periods
	while (fileName.endsWith('.')) {
		fileName = fileName.slice(0, -1);
	}

	const parts = fileName.split('.');

	return parts.pop();
}

export function fileExtensionToFileType(extension: string): FileType {
	const possibleFileTypes: string[] = Object.values(FileType);

	const upcasedExtension = extension.toUpperCase();

	if (possibleFileTypes.includes(upcasedExtension)) {
		return upcasedExtension as FileType;
	} else {
		return FileType.OTHER;
	}
}

export function readFileTypeFromExtension(fileName: string): FileType {
	const extension = readFileExtension(fileName);
	return extension ? fileExtensionToFileType(extension) : FileType.OTHER;
}

export function priceTypeLabel(pt: PricingType) {
	switch (pt) {
		case 'SQUAREFOOT':
			return 'per sqft';
		case 'EACH':
			return 'each';
		case 'HOURLY':
			return 'per hour';
		case 'LINEAR_INCH':
			return 'per linear foot';
		case 'UNITED_INCH':
			return 'per united foot';
		case 'PER_SQUARE':
			return 'per square';
	}
	return '';
}

export function formatFileName(fileName: string): string {
	const hyphenatedFileName = fileName.replace(/\s+/gi, '-'); // replace spaces with hyphens

	// remove special characters S3 hates
	const sanitizedFileName = hyphenatedFileName.replace(/[^\w.-]/g, '');

	// encode any stragglers
	return encodeURIComponent(sanitizedFileName);
}

export function getTaskButtonLabel(jobType: FieldJobType | ScheduledJobType): string {
	if (typeof jobType === 'string') {
		switch (jobType) {
			case ScheduledJobType.MEASURE:
				return 'Measurement';
			case ScheduledJobType.FABRICATION:
				return 'Fabricate';
			case ScheduledJobType.INSTALL:
				return 'Install';
			default:
				return capitalizeFirstLetter(jobType.toLowerCase());
		}
	} else {
		switch (jobType) {
			case FieldJobType.MEASUREMENT:
				return 'Take Measurement';
			case FieldJobType.PURCHASE_ORDER:
				return 'Inspect PO';
			case FieldJobType.FABRICATION:
				return 'Fabricate';
			case FieldJobType.INSTALL:
				return 'Install';
			default:
				return `Start ${jobType}` || 'Start';
		}
	}
}

export function sanitizeTextForLabelDetection(text?: string): string {
	return text ? text.replace(/[^a-z0-9'"\-\s]/gi, '').trim() : '';
}

interface SupplierAddressInput {
	address: IAddress | null;
	supplier: { name: string };
}

interface SupplierContactInput {
	phone?: string | null;
	email?: string | null;
}

export const generateSupplierAddress = (businessSupplier: SupplierAddressInput, contact?: SupplierContactInput) => {
	return businessSupplier.address
		? `${generateAddressString(businessSupplier.address, '\n', businessSupplier.supplier.name)}${
				contact?.phone ? `\n\n${formatDisplayPhone(contact.phone)}` : ''
			}${!contact?.phone && contact?.email ? `\n` : ''}${contact?.email ? `\n${contact.email}` : ''}`
		: businessSupplier.supplier.name;
};

export const generateSupplierAddressWithContacts = (businessSupplier: SupplierAddressInput, contacts: SupplierContactInput[] = []) => {
	const addressString = businessSupplier.address
		? `${generateAddressString(businessSupplier.address, '\n', businessSupplier.supplier.name)}`
		: businessSupplier.supplier.name;

	const contactDetails = contacts
		.map((contact) => {
			const phone = contact.phone ? `\n${formatDisplayPhone(contact.phone)}` : '';
			const email = contact.email ? `\n${contact.email}` : '';
			return `${phone}${!contact.phone && contact.email ? `\n` : ''}${email}`;
		})
		.join('\n');

	return `${addressString}${contactDetails ? `\n${contactDetails}` : ''}`;
};
